import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientPetPicker } from "@/components/ClientPetPicker";
import { AppointmentCalendar } from "@/components/AppointmentCalendar";
import { Plus, CalendarClock, Stethoscope, Scissors, Home, CheckCircle2, CalendarCog, XCircle, RotateCw, List, CalendarDays, CalendarRange } from "lucide-react";
import { toast } from "sonner";

const SERVICES = [
  { key: "vet", label: "Vet Consultation", icon: Stethoscope },
  { key: "grooming", label: "Grooming", icon: Scissors },
  { key: "boarding", label: "Boarding", icon: Home },
];

export default function Appointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("scheduled");
  const [serviceF, setServiceF] = useState("all");
  const [open, setOpen] = useState(false);
  const [pickedPet, setPickedPet] = useState(null);
  const [d, setD] = useState({ service_type: "vet", date_time: "", reason: "", doctor: "", note: "" });
  const [reschedule, setReschedule] = useState(null);
  const [reDate, setReDate] = useState("");
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  const availableServices = SERVICES.filter((s) => !(s.key === "vet" && user?.brand === "kennelmart"));

  const load = useCallback(async () => {
    const params = {};
    if (tab === "cancelled") params.status = "cancelled";
    if (serviceF !== "all") params.service_type = serviceF;
    const { data } = await api.get("/appointments", { params });
    const filtered = tab === "cancelled" ? data : data.filter((a) => a.status !== "cancelled");
    setItems(filtered.sort((a, b) => new Date(a.date_time) - new Date(b.date_time)));
  }, [tab, serviceF]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pickedPet) { toast.error("Select a client & pet"); return; }
    try {
      await api.post("/appointments", { ...d, patient_id: pickedPet.id, client_id: pickedPet.client_id });
      toast.success("Appointment scheduled");
      setOpen(false); setPickedPet(null); setD({ service_type: "vet", date_time: "", reason: "", doctor: "", note: "" });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const confirm = async (a) => {
    try {
      const { data } = await api.post(`/appointments/${a.id}/confirm`);
      toast.success("Visit confirmed — opening record");
      load();
      const path = data.record_type === "grooming" ? `/grooming/${data.record_id}/edit`
        : data.record_type === "boarding" ? `/boarding/${data.record_id}/edit`
        : `/consultations/${data.record_id}/edit`;
      navigate(path);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const cancel = async (a) => { await api.post(`/appointments/${a.id}/cancel`); toast.success("Appointment cancelled"); load(); };
  const doReschedule = async () => {
    if (!reDate) { toast.error("Pick a new date"); return; }
    await api.put(`/appointments/${reschedule.id}/reschedule`, { date_time: reDate });
    toast.success("Rescheduled"); setReschedule(null); setReDate(""); load();
  };

  const dropReschedule = async (a, newISO) => {
    const prev = a.date_time;
    try {
      await api.put(`/appointments/${a.id}/reschedule`, { date_time: newISO });
      load();
      toast.success(`${a.pet_name} moved to ${new Date(newISO).toLocaleDateString()}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.put(`/appointments/${a.id}/reschedule`, { date_time: prev.slice(0, 16) }); toast("Move undone"); load(); }
            catch (e) { toast.error("Could not undo"); }
          },
        },
      });
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const svc = (k) => SERVICES.find((s) => s.key === k) || SERVICES[0];

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">Appointments</h1>
          <p className="text-sm text-muted-foreground mt-1">Schedule visits, then Confirm to start the service, or reschedule/cancel.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPickedPet(null); }}>
          <Button data-testid="new-appointment-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-2" />New Appointment</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>New Appointment</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Service *</Label>
                <div className={`grid gap-2 ${availableServices.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                  {availableServices.map((s) => {
                    const Icon = s.icon; const active = d.service_type === s.key;
                    return (
                      <button type="button" key={s.key} data-testid={`appt-service-${s.key}`} onClick={() => setD({ ...d, service_type: s.key })}
                        className={`rounded-xl border p-3 text-center transition-all ${active ? "border-primary bg-primary/10" : "border-border/60 hover:border-primary/40"}`}>
                        <Icon className={`h-5 w-5 mx-auto mb-1 ${active ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="text-xs font-medium">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Client & Pet *</Label>
                {pickedPet ? (
                  <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm">
                    <span>{pickedPet.pet_name} · {pickedPet.owner_name}</span>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setPickedPet(null)}>Change</Button>
                  </div>
                ) : (
                  <ClientPetPicker trigger={<Button type="button" variant="outline" className="w-full" data-testid="appt-pick-client">Search client & pet…</Button>} onPick={(p) => setPickedPet(p)} />
                )}
              </div>
              <div className="space-y-1.5"><Label>Date & Time *</Label>
                <Input type="datetime-local" value={d.date_time} onChange={(e) => setD({ ...d, date_time: e.target.value })} required data-testid="appt-datetime" /></div>
              {d.service_type === "vet" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Reason</Label><Input value={d.reason} onChange={(e) => setD({ ...d, reason: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Doctor</Label><Input value={d.doctor} onChange={(e) => setD({ ...d, doctor: e.target.value })} /></div>
                </div>
              )}
              <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} /></div>
              <Button type="submit" className="w-full" data-testid="save-appointment-btn">Schedule</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="scheduled" data-testid="appt-tab-scheduled">Upcoming</TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="appt-tab-cancelled">Cancelled</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={serviceF} onValueChange={setServiceF}>
          <SelectTrigger className="w-48" data-testid="appt-service-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {SERVICES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-lg border border-border p-0.5 bg-card">
          {[["list", List, "List"], ["day", CalendarDays, "Day"], ["week", CalendarRange, "Week"]].map(([v, Icon, lbl]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`appt-view-${v}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-4 w-4" />{lbl}
            </button>
          ))}
        </div>
      </div>

      {view === "list" ? (
      <Card className="p-0 border-border/60 overflow-hidden">
        {items.length === 0 && <div className="p-12 text-center text-muted-foreground"><CalendarClock className="h-10 w-10 mx-auto opacity-40 mb-2" />No {tab} appointments.</div>}
        <div className="divide-y divide-border">
          {items.map((a) => {
            const S = svc(a.service_type); const Icon = S.icon;
            return (
              <div key={a.id} data-testid={`appt-row-${a.id}`} className="px-5 py-4 grid grid-cols-12 items-center gap-3">
                <div className="col-span-2 md:col-span-1">
                  <div className="text-xs text-muted-foreground">{new Date(a.date_time).toLocaleDateString(undefined, { month: "short" }).toUpperCase()}</div>
                  <div className="font-primary text-2xl font-bold leading-none">{new Date(a.date_time).getDate()}</div>
                </div>
                <div className="col-span-10 md:col-span-6">
                  <div className="font-medium flex items-center gap-2"><Icon className="h-4 w-4 text-primary" />{a.pet_name} <span className="text-muted-foreground font-normal">· {a.owner_name}</span></div>
                  <div className="text-xs text-muted-foreground">{S.label} · {new Date(a.date_time).toLocaleString()} {a.reason && <>· {a.reason}</>}
                    {a.was_rescheduled && <span className="ml-2 text-blue-600">· rescheduled ×{a.reschedule_count}</span>}
                    {a.status === "confirmed" && <span className="ml-2 text-emerald-600">· confirmed</span>}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-5 flex md:justify-end gap-2 flex-wrap">
                  {tab === "scheduled" && a.status !== "confirmed" && (
                    <>
                      <Button size="sm" onClick={() => confirm(a)} data-testid={`appt-confirm-${a.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Confirm Visit</Button>
                      <Button size="sm" variant="outline" onClick={() => { setReschedule(a); setReDate(a.date_time?.slice(0, 16) || ""); }} data-testid={`appt-reschedule-${a.id}`}><RotateCw className="h-4 w-4 mr-1" />Reschedule</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(a)} data-testid={`appt-cancel-${a.id}`}><XCircle className="h-4 w-4 mr-1" />Cancel</Button>
                    </>
                  )}
                  {a.status === "confirmed" && a.created_record_id && (
                    <Button size="sm" variant="outline" onClick={() => navigate(a.created_record_type === "grooming" ? `/grooming/${a.created_record_id}/edit` : a.created_record_type === "boarding" ? `/boarding/${a.created_record_id}/edit` : `/consultations/${a.created_record_id}/edit`)}>Open record</Button>
                  )}
                  {tab === "cancelled" && <span className="text-xs px-2 py-1 rounded-full bg-destructive/15 text-destructive">Cancelled</span>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      ) : (
        <AppointmentCalendar items={items} mode={view} cursor={cursor} setCursor={setCursor} onSelect={setSelected} onDropReschedule={dropReschedule} />
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{selected?.pet_name} · {svc(selected?.service_type).label}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                {selected.owner_name} · {new Date(selected.date_time).toLocaleString()}
                {selected.was_rescheduled && <span className="ml-2 text-blue-600">· rescheduled ×{selected.reschedule_count}</span>}
              </div>
              {selected.status === "confirmed" ? (
                <Button className="w-full" onClick={() => { const a = selected; setSelected(null); navigate(a.created_record_type === "grooming" ? `/grooming/${a.created_record_id}/edit` : a.created_record_type === "boarding" ? `/boarding/${a.created_record_id}/edit` : `/consultations/${a.created_record_id}/edit`); }}>Open record</Button>
              ) : selected.status === "cancelled" ? (
                <div className="text-sm text-destructive">This appointment was cancelled.</div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  <Button data-testid="cal-confirm" onClick={() => { const a = selected; setSelected(null); confirm(a); }}><CheckCircle2 className="h-4 w-4 mr-2" />Confirm Visit</Button>
                  <Button variant="outline" data-testid="cal-reschedule" onClick={() => { const a = selected; setSelected(null); setReschedule(a); setReDate(a.date_time?.slice(0, 16) || ""); }}><RotateCw className="h-4 w-4 mr-2" />Reschedule</Button>
                  <Button variant="ghost" className="text-destructive" data-testid="cal-cancel" onClick={() => { const a = selected; setSelected(null); cancel(a); }}><XCircle className="h-4 w-4 mr-2" />Cancel</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reschedule} onOpenChange={(o) => { if (!o) setReschedule(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reschedule appointment</DialogTitle></DialogHeader>
          <Label>New date & time</Label>
          <Input type="datetime-local" value={reDate} onChange={(e) => setReDate(e.target.value)} data-testid="reschedule-date" />
          <Button onClick={doReschedule} className="w-full mt-2" data-testid="confirm-reschedule-btn"><CalendarCog className="h-4 w-4 mr-2" />Reschedule</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
